/**
 * Caps an image file's dimensions before uploading, maintaining aspect ratio.
 * Returns the original File if already under the limit or if resizing would
 * make it larger. Falls back to HTMLCanvasElement on browsers without
 * OffscreenCanvas (Safari < 16.4).
 *
 * For JPEG files, EXIF metadata is extracted from the original, the orientation
 * tag is patched to 1 (Normal — pixels are already correctly oriented via
 * imageOrientation: 'from-image'), and the segment is injected into the output.
 */

const MAX_UPLOAD_DIMENSION = 8000;
const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const JPEG_TYPES = new Set(['image/jpeg', 'image/jpg']);

export async function compressImageForUpload(file: File, maxDimension = MAX_UPLOAD_DIMENSION): Promise<File> {
  if (!COMPRESSIBLE_TYPES.has(file.type)) return file;

  try {
    // 1. Create a temporary bitmap to check the original dimensions.
    // 'from-image' ensures EXIF orientation is applied so width/height reflect
    // the display orientation, not the raw stored orientation.
    const originalBitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    let { width, height } = originalBitmap;

    // 2. If already small enough, return the original file
    if (width <= maxDimension && height <= maxDimension) {
      originalBitmap.close();
      console.log('[compressImageForUpload] skipped (already optimal):', file.name, {
        size: `${(file.size / 1024).toFixed(1)} KB`,
        dimensions: `${width}x${height}`,
      });
      return file;
    }

    const originalWidth = width;
    const originalHeight = height;

    // 3. Calculate new dimensions maintaining aspect ratio
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }

    // 4. Let the browser native image scaler do the heavy lifting.
    // 'from-image' must be set here too so the resized bitmap has correct orientation.
    const resizedBitmap = await createImageBitmap(file, {
      resizeWidth: width,
      resizeHeight: height,
      resizeQuality: 'high',
      imageOrientation: 'from-image',
    });
    originalBitmap.close();

    // 5. Draw onto canvas and export — prefer OffscreenCanvas (no DOM required)
    let blob: Blob | null = typeof OffscreenCanvas !== 'undefined'
      ? await toOffscreenBlob(resizedBitmap, width, height, file.type)
      : await toCanvasBlob(resizedBitmap, width, height, file.type);
    resizedBitmap.close();

    // 6. Preserve EXIF metadata from the original JPEG.
    // The canvas strips all metadata, so we manually re-inject the original EXIF
    // segment with the orientation tag patched to 1 (Normal), since the pixels
    // are already correctly oriented.
    if (blob && JPEG_TYPES.has(file.type)) {
      blob = await preserveJpegExif(file, blob);
    }

    // 7. Safety check — return original if resizing somehow made it larger
    if (!blob || blob.size >= file.size) {
      console.log('[compressImageForUpload] skipped (resize increased size):', file.name);
      return file;
    }

    console.log('[compressImageForUpload]', file.name, {
      before: { size: `${(file.size / 1024).toFixed(1)} KB`, dimensions: `${originalWidth}x${originalHeight}`, aspectRatio: (originalWidth / originalHeight).toFixed(3) },
      after:  { size: `${(blob.size / 1024).toFixed(1)} KB`, dimensions: `${width}x${height}`, aspectRatio: (width / height).toFixed(3) },
      saved:  `${((1 - blob.size / file.size) * 100).toFixed(1)}%`,
    }, file);

    return new File([blob], file.name, { type: file.type, lastModified: Date.now() });
  } catch (err) {
    console.warn('[compressImageForUpload] failed, using original:', file.name, err);
    return file;
  }
}

// ─── EXIF preservation ───────────────────────────────────────────────────────

/**
 * Extracts the EXIF APP1 segment from the original JPEG, patches the
 * orientation tag to 1 (Normal), and injects it into the resized blob,
 * replacing any APP0/APP1 segments the canvas may have added.
 */
async function preserveJpegExif(originalFile: File, resizedBlob: Blob): Promise<Blob> {
  const [origBuffer, resizedBuffer] = await Promise.all([
    originalFile.arrayBuffer(),
    resizedBlob.arrayBuffer(),
  ]);

  const exifSegment = extractAndPatchExif(origBuffer);
  if (!exifSegment) return resizedBlob; // no EXIF in original — return as-is

  return injectExifSegment(resizedBuffer, exifSegment, resizedBlob.type);
}

/**
 * Walks the JPEG marker chain to find the APP1/EXIF segment, copies it,
 * then patches the orientation field to 1 so it matches the already-rotated pixels.
 */
function extractAndPatchExif(buffer: ArrayBuffer): Uint8Array | null {
  const view = new DataView(buffer);
  if (view.getUint16(0) !== 0xFFD8) return null; // not JPEG

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    const segLength = view.getUint16(offset + 2); // includes its own 2 bytes

    if (marker === 0xFFE1) {
      // Confirm "Exif\0\0" header (not XMP, which also uses APP1)
      const isExif =
        offset + 10 <= view.byteLength &&
        view.getUint8(offset + 4) === 0x45 && // E
        view.getUint8(offset + 5) === 0x78 && // x
        view.getUint8(offset + 6) === 0x69 && // i
        view.getUint8(offset + 7) === 0x66;   // f

      if (isExif) {
        const segment = buffer.slice(offset, offset + 2 + segLength);
        patchExifOrientation(segment);
        return new Uint8Array(segment);
      }
    }

    if (marker === 0xFFDA) break; // Start of scan — no more metadata markers
    offset += 2 + segLength;
  }

  return null;
}

/**
 * Parses the TIFF structure inside the EXIF segment and sets the Orientation
 * tag (0x0112) to 1 (Normal) in place.
 */
function patchExifOrientation(exifSegment: ArrayBuffer): void {
  // Segment layout: FF E1 [2-byte length] "Exif\0\0" [TIFF data]
  const TIFF_START = 10;
  const view = new DataView(exifSegment);

  if (TIFF_START + 8 > view.byteLength) return;

  const byteOrderMark = view.getUint16(TIFF_START);
  const littleEndian = byteOrderMark === 0x4949; // 'II' = LE, 'MM' = BE

  const ifd0RelOffset = view.getUint32(TIFF_START + 4, littleEndian);
  const ifd0Offset = TIFF_START + ifd0RelOffset;
  if (ifd0Offset + 2 > view.byteLength) return;

  const entryCount = view.getUint16(ifd0Offset, littleEndian);
  for (let i = 0; i < entryCount; i++) {
    const entryOffset = ifd0Offset + 2 + i * 12;
    if (entryOffset + 12 > view.byteLength) break;

    const tag = view.getUint16(entryOffset, littleEndian);
    if (tag === 0x0112) { // Orientation
      view.setUint16(entryOffset + 8, 1, littleEndian); // 1 = Normal
      return;
    }
  }
}

/**
 * Inserts the EXIF segment right after the JPEG SOI marker (FF D8),
 * stripping any APP0/APP1 segments the canvas added to avoid duplicates.
 */
function injectExifSegment(resizedBuffer: ArrayBuffer, exifSegment: Uint8Array, type: string): Blob {
  const resizedBytes = new Uint8Array(resizedBuffer);
  const payloadStart = skipCanvasAppMarkers(resizedBytes);

  const result = new Uint8Array(2 + exifSegment.length + (resizedBytes.length - payloadStart));
  result.set(resizedBytes.subarray(0, 2), 0);                       // FF D8
  result.set(exifSegment, 2);                                        // our EXIF APP1
  result.set(resizedBytes.subarray(payloadStart), 2 + exifSegment.length); // rest

  return new Blob([result], { type });
}

/**
 * Returns the byte offset just past any APP0 (JFIF) and APP1 (EXIF/XMP)
 * markers that the canvas encoder may have prepended.
 */
function skipCanvasAppMarkers(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 2; // skip FF D8
  while (offset + 4 <= bytes.length) {
    const marker = view.getUint16(offset);
    if (marker === 0xFFE0 || marker === 0xFFE1) {
      offset += 2 + view.getUint16(offset + 2);
    } else {
      break;
    }
  }
  return offset;
}

// ─── Canvas helpers ───────────────────────────────────────────────────────────

function toOffscreenBlob(bitmap: ImageBitmap, width: number, height: number, type: string): Promise<Blob> {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get 2D context from OffscreenCanvas');
  ctx.drawImage(bitmap, 0, 0);
  return canvas.convertToBlob({ type });
}

function toCanvasBlob(bitmap: ImageBitmap, width: number, height: number, type: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
    canvas.toBlob(resolve, type);
  });
}

// ─── Worker-based batch compression ──────────────────────────────────────────

/**
 * Compresses multiple images in parallel using Web Workers, one per file.
 * Falls back to main thread processing on browsers without OffscreenCanvas (Safari < 16.4).
 */
export function compressImagesInWorkers(files: File[], maxDimension?: number): Promise<File[]> {
  // OffscreenCanvas is required inside the worker — fall back to main thread if unavailable
  if (typeof OffscreenCanvas === 'undefined') {
    console.log('[compressImagesInWorkers] OffscreenCanvas unavailable, running on main thread');
    return Promise.all(files.map(f => compressImageForUpload(f, maxDimension)));
  }

  return Promise.all(
    files.map(file => new Promise<File>((resolve) => {
      const worker = new Worker(new URL('./imageWorker', import.meta.url), { type: 'module' });
      worker.onmessage = ({ data }: MessageEvent<{ result?: File; error?: string }>) => {
        if (data.error) console.warn('[compressImagesInWorkers] worker error:', data.error);
        resolve(data.result ?? file);
        worker.terminate();
      };
      worker.onerror = (err) => {
        console.warn('[compressImagesInWorkers] worker crashed:', err);
        resolve(file);
        worker.terminate();
      };
      worker.postMessage({ file, maxDimension });
    }))
  );
}
